import React from 'react';
import ReactMapGl, { Marker, MarkerDragEvent } from 'react-map-gl';
import { useQuery } from 'react-query';
import { Cities, IVenue } from 'btdt-types';

const SNAP_THRESHOLD = 0.00015; // ~15 meters

interface IProps {
  value: [number, number];
  onChange?: (newCoordinates: [number, number]) => void;
  venueId?: number;
  city?: Cities;
}

const CoordinatesEditor = ({ value, onChange, venueId, city }: IProps) => {
  const [snapped, setSnapped] = React.useState(false);
  const shiftHeld = React.useRef(false);

  const { data: allVenues } = useQuery<IVenue[]>('all-venues', { enabled: false });

  const sameCityVenues = React.useMemo(() => {
    if (!allVenues || !city) return [];
    return allVenues.filter(
      (v) => v.city === city && v.id !== venueId && v.coordinates
    );
  }, [allVenues, city, venueId]);

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Shift') shiftHeld.current = true; };
    const handleKeyUp = (e: KeyboardEvent) => { if (e.key === 'Shift') shiftHeld.current = false; };
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const handleDragEnd = (event: MarkerDragEvent) => {
    const dropped: [number, number] = [event.lngLat.lat, event.lngLat.lng];

    if (!shiftHeld.current && sameCityVenues.length > 0) {
      let closestDist = Infinity;
      let closestCoords: [number, number] | null = null;

      for (const v of sameCityVenues) {
        const dLat = dropped[0] - v.coordinates[0];
        const dLng = dropped[1] - v.coordinates[1];
        const dist = Math.sqrt(dLat * dLat + dLng * dLng);
        if (dist < closestDist) {
          closestDist = dist;
          closestCoords = v.coordinates;
        }
      }

      if (closestDist < SNAP_THRESHOLD && closestCoords) {
        setSnapped(true);
        setTimeout(() => setSnapped(false), 600);
        onChange?.(closestCoords);
        return;
      }
    }

    onChange?.(dropped);
  };

  return (
    <ReactMapGl
      initialViewState={{
        longitude: value[1],
        latitude: value[0],
        zoom: 16,
      }}
      style={{ width: '100%', height: '100%' }}
      mapStyle="mapbox://styles/mapbox/dark-v11"
      mapboxAccessToken="pk.eyJ1IjoiYW1pcm96ZXIiLCJhIjoiY2xiODN3OHp5MDgzdzNvbXRzbHRmNDh6byJ9.z32MJnbhRaPU0qEXgjq7bQ"
    >
      {sameCityVenues.map((v) => (
        <Marker
          key={v.id}
          longitude={v.coordinates[1]}
          latitude={v.coordinates[0]}
          color="#666666"
        />
      ))}
      <Marker
        longitude={value[1]}
        latitude={value[0]}
        color={snapped ? '#00ff88' : '#ccaa00'}
        draggable={Boolean(onChange)}
        onDragEnd={handleDragEnd}
      />
    </ReactMapGl>
  );
};

export default CoordinatesEditor;
